import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, TrendingUp, AlertCircle, CheckCircle,
  Plus, Search, Filter, Calendar, DollarSign, Users, Target
} from 'lucide-react';
import MetricCard from '../components/common/MetricCard';
import ProgressBar from '../components/common/ProgressBar';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import { Button } from '../components/ui/button';

const EnhancedSponsorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Fetch dashboard summary
      const summaryRes = await fetch(`${apiUrl}/api/projects/dashboard/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const summary = await summaryRes.json();

      // Fetch projects list
      const projectsRes = await fetch(`${apiUrl}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectsList = await projectsRes.json();

      // Fetch match suggestions
      const matchRes = await fetch(`${apiUrl}/api/matching/suggestions?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const matches = await matchRes.json();

      setDashboardData(summary);
      setProjects(projectsList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesFilter = filter === 'all' || project.status === filter;
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.projectCode.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sponsor Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage your CSR projects and track impact</p>
            </div>
            <Button
              onClick={() => navigate('/projects/create')}
              className="text-white"
              style={{ background: '#14B8A6' }}
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Project
            </Button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Projects"
            value={dashboardData?.totalProjects || 0}
            subtitle={`${dashboardData?.activeProjects || 0} active`}
            icon={Briefcase}
            color="#14B8A6"
          />
          <MetricCard
            title="Total Budget"
            value={`₹${((dashboardData?.totalBudget || 0) / 100000).toFixed(1)}L`}
            subtitle={`${((dashboardData?.utilizationRate || 0)).toFixed(0)}% utilized`}
            icon={DollarSign}
            color="#1E3A5F"
            trend={dashboardData?.utilizationRate >= 80 ? 1 : dashboardData?.utilizationRate >= 50 ? 0 : -1}
          />
          <MetricCard
            title="Completed"
            value={dashboardData?.completedProjects || 0}
            subtitle="Projects finished"
            icon={CheckCircle}
            color="#10B981"
          />
          <MetricCard
            title="High Priority Alerts"
            value={dashboardData?.alerts?.highPriority || 0}
            subtitle="Requires attention"
            icon={AlertCircle}
            color="#EF4444"
          />
        </div>

        {/* Alerts Section */}
        {dashboardData?.alerts?.total > 0 && (
          <AlertBanner
            type="warning"
            message={`You have ${dashboardData.alerts.total} active alerts. ${dashboardData.alerts.highPriority} require immediate attention.`}
            className="mb-6"
          />
        )}

        {/* Projects Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Projects</h2>
            <div className="flex gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              {/* Filter */}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {/* Projects List */}
          {filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No projects found</h3>
              <p className="text-gray-600 mb-6">Create your first CSR project to get started</p>
              <Button
                onClick={() => navigate('/projects/create')}
                className="text-white"
                style={{ background: '#14B8A6' }}
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Project
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} navigate={navigate} />
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickActionCard
            icon={Users}
            title="Find NGO Partners"
            description="Discover verified NGOs matched to your CSR goals"
            onClick={() => navigate('/matching/browse')}
            color="#14B8A6"
          />
          <QuickActionCard
            icon={Calendar}
            title="Compliance Calendar"
            description="Track deadlines and generate required documents"
            onClick={() => navigate('/compliance/calendar')}
            color="#1E3A5F"
          />
          <QuickActionCard
            icon={Target}
            title="Impact Dashboard"
            description="View comprehensive impact metrics and reports"
            onClick={() => navigate('/impact/dashboard')}
            color="#10B981"
          />
        </div>
      </div>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, navigate }) => {
  return (
    <div 
      className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-gray-900">{project.title}</h3>
            <StatusBadge status={project.status} size="sm" />
          </div>
          <p className="text-sm text-gray-600 mb-2">{project.projectCode}</p>
          <p className="text-sm text-gray-700">{project.scheduleVIICategory}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-teal-600">
            ₹{(project.totalBudget / 100000).toFixed(1)}L
          </p>
          <p className="text-xs text-gray-500">Budget</p>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <ProgressBar
          progress={project.completionPercentage || 0}
          label="Completion"
          color="auto"
          height="h-2"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
        <div>
          <p className="text-xs text-gray-500 mb-1">Utilized</p>
          <p className="text-sm font-semibold text-gray-900">
            ₹{(project.fundsUtilized / 100000).toFixed(1)}L
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Duration</p>
          <p className="text-sm font-semibold text-gray-900">{project.duration} months</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Location</p>
          <p className="text-sm font-semibold text-gray-900">{project.location?.state}</p>
        </div>
      </div>

      {/* Alerts */}
      {project.alerts && project.alerts.length > 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm text-orange-600">
          <AlertCircle className="w-4 h-4" />
          <span>{project.alerts.length} alert{project.alerts.length > 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard = ({ icon: Icon, title, description, onClick, color }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all cursor-pointer group"
    >
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
};

export default EnhancedSponsorDashboard;
